import { useState, useEffect, useRef } from "react";
import { casualData } from "../api/casualData.js";

// Duomenų matricos lentelė, iš kurios gyvai skaitoma patalpų konfigūracija
// (žr. roomsConfig state komponento viduje).
const ROOMS_CONFIG_TABLE_NAME = "SKAIČIUOKLĖS PATALPOS";

export const HOUSE_TYPES = [
  { id: "house1", label: "1 aukšto namas", icon: "🏠", info: "Namo sąvokai priskiriami ir kotedžai" },
  { id: "house2", label: "2 aukštų namas", icon: "🏡", info: "Namo sąvokai priskiriami ir kotedžai" },
  { id: "apartment", label: "Butas", icon: "🏢" },
];

export const SYSTEMS = [
  { id: "kanalizacija", label: "Kanalizacija", icon: "🚰" },
  { id: "vandentiekis", label: "Vandentiekis", icon: "🚿" },
  { id: "grindinis_sildymas", label: "Grindinis šildymas", icon: "🔥" },
  { id: "rekuperacija", label: "Rekuperacija", icon: "💨" },
  { id: "kondicionavimas", label: "Kondicionavimas", icon: "❄️" },
  { id: "santechnika", label: "Santechnikos prietaisai", icon: "🛁" },
];

// TODO: aprašymai yra tik vietaženkliai — pakeisti tikru kiekvienos klasės
// aprašymu, kai vartotojas jį pateiks.
export const CLASS_OPTIONS = [
  { id: "eco", label: "ECO", icon: "🌱", info: "ECO klasės aprašymas (vietaženklis)" },
  { id: "smart", label: "SMART", icon: "⚡", info: "SMART klasės aprašymas (vietaženklis)" },
  { id: "premium", label: "PREMIUM", icon: "⭐", info: "PREMIUM klasės aprašymas (vietaženklis)" },
  { id: "ultra", label: "ULTRA", icon: "💎", info: "ULTRA klasės aprašymas (vietaženklis)" },
];

// Visi galimi patalpų tipai — pasirenkami (vilkiant arba spaudžiant) iš sąrašo dešinėje.
// Eksportuojama, kad Duomenų matricos "Kategorija: Patalpos" stulpelis galėtų
// naudoti tą patį sąrašą.
export const ROOM_TYPES = [
  "Tambūras", "Koridorius", "Svetainė/virtuvė", "Tualetas", "Tėvų kambarys",
  "Vaikų kambarys", "Drabužinė", "Katilinė", "Laiptinė", "Sandėliukas",
  "Darbo kambarys", "Garažas", "Laisvalaikio kambarys",
];

// Patalpų numatytoji konfigūracija (kuriuos tipus pasiūlyti kuriam aukštui ir
// kurie pridedami pagal nutylėjimą) nebėra užkoduota kode — ji gyvai skaitoma
// iš Duomenų matricos lentelės "SKAIČIUOKLĖS PATALPOS" (žr. ROOMS_CONFIG_TABLE_NAME
// žemiau, komponento viduje). `roomsConfig` elementas: {name, defaultArea, floor1, floor2, isDefault}.

function seedRooms(types, roomsConfig) {
  return types.map(type => {
    const cfg = roomsConfig.find(r => r.name === type);
    return { id: Date.now() + Math.random(), type, area: cfg?.defaultArea ? String(cfg.defaultArea) : "" };
  });
}

// Santechnikos prietaisų tipai, pasirenkami tualetams (kaip patalpų sąraše — kortelė + drag&drop).
// Šie trys prietaisų sąrašai taip pat eksportuojami Duomenų matricos
// "Kategorija: Prietaisai" stulpeliui (žr. DuomenuMatricaView.jsx).
export const TOILET_FIXTURE_TYPES = [
  "Unitazas", "Unitazas su bidė maišytuvu", "Dušas", "Vonia",
  "El. gyvatukas", "Kriauklė", "Skalbimo mašina", "Džiovyklė",
  "Bidė", "Tropinis lietus", "Pisuaras", "Dviguba kriauklė",
];

// Katilinės įrangos tipai.
export const BOILER_FIXTURE_TYPES = [
  "Katilas/siurblys", "Buferinė talpa", "Vandens įvadas", "Vandens paskirstymo mazgas", "Rekuperatorius",
  "Skalbimo mašina", "Džiovyklė", "Lauko laistymas", "Lauko čiaupas",
  "Geriamojo vandens filtras", "Buitinio vandens filtras",
];

// Virtuvės prietaisų tipai.
export const KITCHEN_FIXTURE_TYPES = ["Virtuvinė plautuvė", "Indaplovė", "Kranas virš kaitlentės", "Geriamojo vandens filtrai"];

// "Katilas/siurblys" pozicijai reikia patikslinti konkretų tipą.
const BOILER_SUBTYPES = [
  "Šilumos siurblys oras/vanduo", "Šilumos siurblys oras/oras",
  "Dujinis katilas", "Kieto kuro katilas", "Elektrinis katilas",
];

const MOUNT_TYPES = ["Potinkinis", "Virštinkinis"];
const TOILET_MOUNT_TYPES = ["Pastatomas", "Pakabinamas"];

// Kai kurioms prietaisų pozicijoms reikia patikslinti tipą — bendras žemėlapis,
// naudojamas FixtureRow tiek santechnikai, tiek katilinės įrangai.
const FIXTURE_SUBTYPE_OPTIONS = {
  "Katilas/siurblys": BOILER_SUBTYPES,
  "Unitazas": TOILET_MOUNT_TYPES,
  "Vonia": MOUNT_TYPES,
  "Dušas": MOUNT_TYPES,
  "Kriauklė": MOUNT_TYPES,
  "Dviguba kriauklė": MOUNT_TYPES,
  "Unitazas su bidė maišytuvu": MOUNT_TYPES,
  "Bidė": MOUNT_TYPES,
  "Virtuvinė plautuvė": MOUNT_TYPES,
};

// Kiekvieno būsto tipo tualetų numatytoji santechnikos konfigūracija (redaguojama).
// house2 skiriasi pagal aukštą, todėl floor1/floor2 laikomi atskirai; house1/apartment
// naudoja tik floor1 (jų tualetai visada saugomi roomsFloor1).
const DEFAULT_TOILET_FIXTURES = {
  house1: { floor1: ["Unitazas", "Dušas", "El. gyvatukas", "Kriauklė"] },
  house2: {
    floor1: ["Unitazas su bidė maišytuvu", "Dušas", "El. gyvatukas", "Kriauklė"],
    floor2: ["Unitazas", "Vonia", "El. gyvatukas", "Kriauklė"],
  },
  apartment: { floor1: ["Unitazas", "Dušas", "Kriauklė", "Skalbimo mašina", "Džiovyklė", "El. gyvatukas"] },
};

// Katilinės numatytoji įranga — ta pati visais atvejais.
const DEFAULT_BOILER_FIXTURES = ["Katilas/siurblys", "Buferinė talpa", "Vandens įvadas", "Vandens paskirstymo mazgas", "Rekuperatorius"];

// Virtuvės numatytoji konfigūracija — ta pati visais atvejais.
const DEFAULT_KITCHEN_FIXTURES = ["Virtuvinė plautuvė", "Indaplovė"];

function seedFixtures(types) {
  return types.map(type => ({ id: Date.now() + Math.random(), type, qty: 1, subtype: FIXTURE_SUBTYPE_OPTIONS[type] ? "" : undefined }));
}

// Apibrėžta už komponento ribų — žr. ProfilisView/CasualProfileView pastabą:
// viduje apibrėžta funkcija taptų nauju komponento tipu kiekviename render'e.
function InfoTooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={e => e.stopPropagation()}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 16, height: 16, borderRadius: "50%", background: "#e5e7eb", color: "#6b7280",
        fontSize: 11, fontWeight: 700, cursor: "help",
      }}>i</span>
      {show && (
        <span style={{
          position: "absolute", bottom: "130%", left: "50%", transform: "translateX(-50%)",
          background: "#383853", color: "#fff", fontSize: 11, padding: "6px 10px", borderRadius: 6,
          whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", zIndex: 30,
        }}>
          {text}
        </span>
      )}
    </span>
  );
}

function SelectCard({ selected, onClick, icon, label, info }) {
  return (
    <div
      onClick={onClick}
      style={{
        position: "relative", flex: "1 1 140px", minWidth: 140, cursor: "pointer",
        border: selected ? "2px solid #735DFF" : "1px solid #e5e7eb",
        background: selected ? "var(--ral3000-subtle)" : "#fff",
        borderRadius: 12, padding: "20px 16px", textAlign: "center",
        transition: "border-color .15s, background .15s",
      }}
    >
      {info && (
        <div style={{ position: "absolute", top: 10, right: 10 }}>
          <InfoTooltip text={info} />
        </div>
      )}
      <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: selected ? "var(--ral3000)" : "#1c1c1c" }}>{label}</div>
    </div>
  );
}

function ClassCard({ selected, onClick, icon, label, info }) {
  const [hovered, setHovered] = useState(false);
  const highlighted = selected || hovered;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative", width: 360, height: 630, cursor: "pointer",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        border: highlighted ? "2px solid #735DFF" : "1px solid #d1d5db",
        boxShadow: highlighted ? "0 0 0 3px rgba(115,93,255,0.15)" : "none",
        background: selected ? "var(--ral3000-subtle)" : "#F0F1F5",
        borderRadius: 12, padding: "20px 16px", textAlign: "center",
        transition: "border-color .15s, background .15s, box-shadow .15s",
      }}
    >
      {info && (
        <div style={{ position: "absolute", top: 14, right: 14 }}>
          <InfoTooltip text={info} />
        </div>
      )}
      <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: highlighted ? "var(--ral3000)" : "#1c1c1c" }}>{label}</div>
    </div>
  );
}

function NextButton({ onClick, disabled, label = "Toliau →" }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: disabled ? "#e5e7eb" : "#735DFF", color: disabled ? "#9ca3af" : "#fff",
        border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {label}
    </button>
  );
}

function BackButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      style={{ background: "#f3f4f6", color: "#383853", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
    >
      ← Atgal
    </button>
  );
}

function AreaField({ label, value, onChange, unit = "m²" }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4 }}>{label}</label>
      <div style={{ position: "relative", maxWidth: 220 }}>
        <input
          type="number" min="0" step="0.01" value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="0"
          style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "9px 34px 9px 12px", fontSize: 14, boxSizing: "border-box" }}
        />
        <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#9ca3af" }}>{unit}</span>
      </div>
    </div>
  );
}

// Patalpos kortelė dešinės kolonos sąraše — vilkiama (HTML5 drag&drop) arba
// paspaudžiama, kad pridėtų naują patalpą (galima pridėti tą patį tipą kelis kartus).
function RoomTypeCard({ type, onAdd }) {
  return (
    <div
      draggable
      onDragStart={e => e.dataTransfer.setData("text/plain", type)}
      onClick={onAdd}
      style={{
        border: "1px solid #e5e7eb", borderRadius: 8, padding: "9px 10px", marginBottom: 6,
        fontSize: 12, fontWeight: 600, color: "#383853", cursor: "grab", background: "#fff", userSelect: "none",
      }}
      title="Vilkite į kairę arba spauskite, kad pridėtumėte"
    >
      {type}
    </div>
  );
}

// Aukšto pavadinimo eilutė + likęs neišskirstytas plotas (arba trumpas įspėjimas,
// jei buvo bandyta viršyti aukšto bendrą plotą).
function FloorHeader({ label, remaining, warning }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: warning ? "#dc2626" : "#383853" }}>
        {warning ? warning : `Likęs plotas: ${remaining.toFixed(2)} m²`}
      </div>
    </div>
  );
}

// Vienos patalpos eilutė: tipas + (jei pasirinktas kondicionavimas) priskyrimo
// snaigės mygtukas + plotas (iki 2 skaičių po kablelio) + šalinimo mygtukas.
function RoomRow({ room, onAreaChange, onRemove, showAC, onToggleAC }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
      <span style={{ flex: 1, fontSize: 13, color: "#1c1c1c" }}>{room.type}</span>
      {showAC && (
        <button onClick={onToggleAC}
          title={room.ac ? "Pašalinti kondicionavimo priskyrimą" : "Priskirti kondicionavimą"}
          style={{
            width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center",
            borderRadius: 6, border: room.ac ? "1px solid #60a5fa" : "1px solid #e5e7eb",
            background: room.ac ? "#dbeafe" : "#fff", cursor: "pointer", fontSize: 13, padding: 0,
          }}>
          ❄️
        </button>
      )}
      <div style={{ position: "relative" }}>
        <input
          type="number" min="0" step="0.01" value={room.area}
          onChange={e => onAreaChange(e.target.value)}
          placeholder="0.00"
          style={{ width: 90, border: "1px solid #e5e7eb", borderRadius: 6, padding: "5px 26px 5px 8px", fontSize: 13, boxSizing: "border-box" }}
        />
        <span style={{ position: "absolute", right: 7, top: "50%", transform: "translateY(-50%)", fontSize: 11, color: "#9ca3af" }}>m²</span>
      </div>
      <button onClick={onRemove} style={{ background: "none", border: "none", color: "#d1d5db", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
    </div>
  );
}

// Vienos grindų (ar viso būsto, jei ne dviejų aukštų) patalpų sąrašas — kartu yra
// ir drag&drop numetimo zona.
function RoomList({ rooms, onDrop, onAreaChange, onRemove, showAC, onToggleAC }) {
  const total = rooms.reduce((a, r) => a + (parseFloat(r.area) || 0), 0);
  return (
    <div
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.preventDefault(); const type = e.dataTransfer.getData("text/plain"); if (type) onDrop(type); }}
      style={{ border: "1px dashed #d1d5db", borderRadius: 10, padding: 12, minHeight: 70 }}
    >
      {rooms.length === 0 && (
        <div style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: "14px 0" }}>
          Vilkite arba spauskite patalpos tipą iš sąrašo dešinėje
        </div>
      )}
      {rooms.map(r => (
        <RoomRow key={r.id} room={r}
          onAreaChange={v => onAreaChange(r.id, v)}
          onRemove={() => onRemove(r.id)}
          showAC={showAC}
          onToggleAC={() => onToggleAC && onToggleAC(r.id)} />
      ))}
      {rooms.length > 0 && (
        <div style={{ textAlign: "right", fontSize: 12, fontWeight: 700, color: "#383853", marginTop: 8 }}>
          Iš viso: {total.toFixed(2)} m²
        </div>
      )}
    </div>
  );
}

// Vienos santechnikos/katilinės pozicijos eilutė: tipas + (jei pozicija turi
// apibrėžtus patikslinimo variantus FIXTURE_SUBTYPE_OPTIONS) tipo pasirinkimas
// + kiekis + šalinimo mygtukas.
function FixtureRow({ fixture, onQtyChange, onSubtypeChange, onRemove }) {
  const subtypeOptions = FIXTURE_SUBTYPE_OPTIONS[fixture.type];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
      <span style={{ flex: 1, fontSize: 13, color: "#1c1c1c" }}>{fixture.type}</span>
      {subtypeOptions && (
        <select value={fixture.subtype || ""} onChange={e => onSubtypeChange(e.target.value)}
          style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 6px", fontSize: 11, maxWidth: 180 }}>
          <option value="">Pasirinkite tipą</option>
          {subtypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      )}
      <input
        type="number" min="0" step="1" value={fixture.qty}
        onChange={e => onQtyChange(e.target.value)}
        placeholder="0"
        style={{ width: 55, border: "1px solid #e5e7eb", borderRadius: 6, padding: "5px 8px", fontSize: 13, boxSizing: "border-box" }}
      />
      <span style={{ fontSize: 11, color: "#9ca3af" }}>vnt.</span>
      <button onClick={onRemove} style={{ background: "none", border: "none", color: "#d1d5db", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
    </div>
  );
}

// Prietaiso kortelė dešinės kolonos sąraše — vilkiama (HTML5 drag&drop) arba
// paspaudžiama, kad pridėtų naują prietaisą (ta pati logika kaip RoomTypeCard).
function FixtureTypeCard({ type, onAdd }) {
  return (
    <div
      draggable
      onDragStart={e => e.dataTransfer.setData("text/plain", type)}
      onClick={onAdd}
      style={{
        border: "1px solid #e5e7eb", borderRadius: 8, padding: "9px 10px", marginBottom: 6,
        fontSize: 12, fontWeight: 600, color: "#383853", cursor: "grab", background: "#fff", userSelect: "none",
      }}
      title="Vilkite į kairę arba spauskite, kad pridėtumėte"
    >
      {type}
    </div>
  );
}

// Vieno kambario (tualeto ar katilinės) prietaisų sąrašas — kartu yra ir
// drag&drop numetimo zona (ta pati logika kaip RoomList).
function FixtureList({ fixtures, onDrop, onQtyChange, onSubtypeChange, onRemove }) {
  return (
    <div
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.preventDefault(); const type = e.dataTransfer.getData("text/plain"); if (type) onDrop(type); }}
      style={{ border: "1px dashed #d1d5db", borderRadius: 10, padding: 12, minHeight: 60 }}
    >
      {fixtures.length === 0 && (
        <div style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: "14px 0" }}>
          Vilkite arba spauskite prietaisą iš sąrašo dešinėje
        </div>
      )}
      {fixtures.map(f => (
        <FixtureRow key={f.id} fixture={f}
          onQtyChange={v => onQtyChange(f.id, v)}
          onSubtypeChange={v => onSubtypeChange(f.id, v)}
          onRemove={() => onRemove(f.id)} />
      ))}
    </div>
  );
}

function makeEstimateCode() {
  return "SM-" + Date.now().toString(36).toUpperCase();
}

// gId/props: initialEstimate (jei redaguojama jau esanti sąmata) + onUpsert (išsaugo
// sąmatą į "Mano sąmatos" sąrašą). Tėvinis komponentas turi perduoti `key` pagal
// sąmatos id, kad pakeitus redaguojamą sąmatą komponentas būtų pilnai perkraunamas
// (kitaip šis initialEstimate būtų naudojamas tik pirmam render'ui).
export function SkaiciuokleView({ initialEstimate, onUpsert }) {
  const isEditing = !!initialEstimate;
  const [started, setStarted] = useState(isEditing);
  const [estimateId, setEstimateId] = useState(initialEstimate?.id ?? null);
  const [estimateCode, setEstimateCode] = useState(initialEstimate?.code ?? null);
  const [estimateName, setEstimateName] = useState(initialEstimate?.name ?? null);
  const [createdAt, setCreatedAt] = useState(initialEstimate?.createdAt ?? null);
  const [step, setStep] = useState(initialEstimate?.step ?? 1);
  const [houseType, setHouseType] = useState(initialEstimate?.houseType ?? null);
  const [area1, setArea1] = useState(initialEstimate?.area1 ?? "");
  const [area2, setArea2] = useState(initialEstimate?.area2 ?? "");
  const [ceilingHeight1, setCeilingHeight1] = useState(initialEstimate?.ceilingHeight1 ?? "");
  const [ceilingHeight2, setCeilingHeight2] = useState(initialEstimate?.ceilingHeight2 ?? "");
  const [systems, setSystems] = useState(initialEstimate?.systems ?? []);
  const [roomsFloor1, setRoomsFloor1] = useState(initialEstimate?.roomsFloor1 ?? []);
  const [roomsFloor2, setRoomsFloor2] = useState(initialEstimate?.roomsFloor2 ?? []);
  const [addFloor, setAddFloor] = useState(1);
  const [floorWarnings, setFloorWarnings] = useState({});
  const warnTimeouts = useRef({});
  // { [tualeto/katilinės room.id]: [{id, type, qty, subtype?}] }
  const [toiletFixtures, setToiletFixtures] = useState(initialEstimate?.toiletFixtures ?? {});
  const [boilerFixtures, setBoilerFixtures] = useState(initialEstimate?.boilerFixtures ?? {});
  const [kitchenFixtures, setKitchenFixtures] = useState(initialEstimate?.kitchenFixtures ?? {});
  const [activeFixtureTarget, setActiveFixtureTarget] = useState(null); // {kind:'toilet'|'boiler'|'kitchen', roomId}
  const [selectedClass, setSelectedClass] = useState(initialEstimate?.selectedClass ?? null);
  const [roomsConfig, setRoomsConfig] = useState([]);

  // Patalpų konfigūracija (kurie tipai siūlomi kuriam aukštui, kurie pridedami
  // pagal nutylėjimą ir jų default kvadratūra) užkraunama vieną kartą iš
  // Duomenų matricos lentelės "SKAIČIUOKLĖS PATALPOS" — žr. ROOMS_CONFIG_TABLE_NAME.
  useEffect(() => {
    let cancelled = false;
    async function loadRoomsConfig() {
      try {
        const tables = await casualData.listDataMatrixTables();
        const match = (Array.isArray(tables) ? tables : []).find(t => t.name === ROOMS_CONFIG_TABLE_NAME);
        if (!match) return;
        const full = await casualData.getPriceTable(match.id);
        const colByName = Object.fromEntries((full.columns || []).map(c => [c.name, c.id]));
        const nameCol = colByName["Patalpos pavadinimas"];
        const areaCol = colByName["default Kvadratūra"];
        const floor1Col = colByName["Pirmas aukštas"];
        const floor2Col = colByName["Antras aukštas"];
        const pickCol = colByName["Pasirinkimas"];
        const orderCol = colByName["Eil. Nr."];
        if (!nameCol) return;
        const parsed = (full.rows || [])
          .map(r => ({
            order: orderCol ? (parseFloat(r.cells[orderCol]) || 0) : 0,
            name: r.cells[nameCol] || "",
            defaultArea: areaCol ? (r.cells[areaCol] ?? "") : "",
            floor1: floor1Col ? !!r.cells[floor1Col] : false,
            floor2: floor2Col ? !!r.cells[floor2Col] : false,
            isDefault: pickCol ? !!r.cells[pickCol] : false,
          }))
          .filter(r => r.name)
          .sort((a, b) => a.order - b.order);
        if (!cancelled) setRoomsConfig(parsed);
      } catch (e) {
        console.error("Failed to load rooms config from Duomenų matrica", e);
      }
    }
    loadRoomsConfig();
    return () => { cancelled = true; };
  }, []);

  function toggleSystem(id) {
    setSystems(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  }

  // Sukuriama nauja sąmata ir iškart įtraukiama į "Mano sąmatos" sąrašą (kaip
  // juodraštis), kad ji ten matytųsi net jei vartotojas vedlio nebaigtų.
  function handleStart() {
    const id = Date.now() + Math.random();
    const code = makeEstimateCode();
    const name = "";
    const now = new Date().toISOString();
    setEstimateId(id);
    setEstimateCode(code);
    setEstimateName(name);
    setCreatedAt(now);
    setStep(1);
    setStarted(true);
    onUpsert({
      id, code, name, status: "draft", createdAt: now, updatedAt: now, step: 1,
      houseType: null, area1: "", area2: "", ceilingHeight1: "", ceilingHeight2: "",
      systems: [], roomsFloor1: [], roomsFloor2: [], toiletFixtures: {}, boilerFixtures: {}, kitchenFixtures: {},
    });
  }

  // Būsto tipo pasirinkimas užpildo numatytąją patalpų konfigūraciją (o ne
  // useEffect ant houseType — tai susikirstų su esamos sąmatos duomenų užkrovimu).
  // Numatytasis sąrašas vienodas visiems būsto tipams (žr. roomsConfig) — 2 aukštas
  // užpildomas tik house2 atveju, nes kiti būsto tipai jo visai nenaudoja.
  function selectHouseType(id) {
    setHouseType(id);
    const floor1Types = roomsConfig.filter(r => r.isDefault && r.floor1).map(r => r.name);
    const floor2Types = id === "house2" ? roomsConfig.filter(r => r.isDefault && r.floor2).map(r => r.name) : [];
    setRoomsFloor1(seedRooms(floor1Types, roomsConfig));
    setRoomsFloor2(seedRooms(floor2Types, roomsConfig));
    setAddFloor(1);
  }

  // Visos vartotojo įvestys įsimenamos — kiekvienas pasikeitimas (su nedideliu
  // debounce) išsaugomas į "Mano sąmatos" sąrašą per onUpsert.
  const pendingSaveRef = useRef(false);
  const latestPayloadRef = useRef(null);

  function buildEstimatePayload() {
    return {
      id: estimateId, code: estimateCode, name: estimateName, status: "draft",
      createdAt: createdAt || new Date().toISOString(), updatedAt: new Date().toISOString(),
      step, houseType, area1, area2, ceilingHeight1, ceilingHeight2,
      systems, roomsFloor1, roomsFloor2, toiletFixtures, boilerFixtures, kitchenFixtures, selectedClass,
    };
  }

  useEffect(() => {
    if (!started || !estimateId) return;
    latestPayloadRef.current = buildEstimatePayload();
    pendingSaveRef.current = true;
    const t = setTimeout(() => {
      pendingSaveRef.current = false;
      onUpsert(latestPayloadRef.current);
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, estimateId, estimateCode, estimateName, createdAt, step, houseType, area1, area2, ceilingHeight1, ceilingHeight2, systems, roomsFloor1, roomsFloor2, toiletFixtures, boilerFixtures, kitchenFixtures, selectedClass]);

  // Jei vedlys išmontuojamas (pvz. vartotojas perjungė meniu skiltį) kol dar
  // laukiama debounce'o — anksčiau tas laukiantis išsaugojimas tiesiog
  // atšaukdavosi be jokio įspėjimo. Dabar paskutinė būsena "flush'inama" iškart.
  useEffect(() => {
    return () => {
      if (pendingSaveRef.current && latestPayloadRef.current) {
        onUpsert(latestPayloadRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTwoFloors = houseType === "house2";
  const a1 = parseFloat(area1) || 0;
  const a2 = parseFloat(area2) || 0;
  const totalArea = isTwoFloors ? a1 + a2 : a1;
  const areaValid = isTwoFloors ? a1 > 0 && a2 > 0 : a1 > 0;
  const usedFloor1 = roomsFloor1.reduce((s, r) => s + (parseFloat(r.area) || 0), 0);
  const usedFloor2 = roomsFloor2.reduce((s, r) => s + (parseFloat(r.area) || 0), 0);
  const remainingFloor1 = a1 - usedFloor1;
  const remainingFloor2 = a2 - usedFloor2;

  function addRoom(floor, type) {
    const setter = floor === 2 ? setRoomsFloor2 : setRoomsFloor1;
    const cfg = roomsConfig.find(r => r.name === type);
    const area = cfg?.defaultArea ? String(cfg.defaultArea) : "";
    setter(prev => [...prev, { id: Date.now() + Math.random(), type, area }]);
  }
  function removeRoom(floor, id) {
    const setter = floor === 2 ? setRoomsFloor2 : setRoomsFloor1;
    setter(prev => prev.filter(r => r.id !== id));
  }
  // Kondicionavimo priskyrimas patalpai — kur bus naudojamas, paaiškės vėliau;
  // čia tik įsimenama, kurioms patalpoms jis priskirtas.
  function toggleRoomAC(floor, id) {
    const setter = floor === 2 ? setRoomsFloor2 : setRoomsFloor1;
    setter(prev => prev.map(r => r.id === id ? { ...r, ac: !r.ac } : r));
  }

  function showFloorWarning(floor) {
    setFloorWarnings(prev => ({ ...prev, [floor]: "Viršytas aukšto plotas — reikšmė sumažinta" }));
    if (warnTimeouts.current[floor]) clearTimeout(warnTimeouts.current[floor]);
    warnTimeouts.current[floor] = setTimeout(() => {
      setFloorWarnings(prev => ({ ...prev, [floor]: null }));
    }, 2500);
  }

  // Neleidžiama, kad aukšto patalpų plotų suma viršytų to aukšto bendrą plotą
  // (įvestą 2 puslapyje) — reikšmė automatiškai apkarpoma iki likusio ploto.
  function updateRoomArea(floor, id, value) {
    const setter = floor === 2 ? setRoomsFloor2 : setRoomsFloor1;
    const rooms = floor === 2 ? roomsFloor2 : roomsFloor1;
    const floorTotal = floor === 2 ? a2 : a1;
    const others = rooms.filter(r => r.id !== id).reduce((s, r) => s + (parseFloat(r.area) || 0), 0);
    const maxAllowed = Math.max(0, floorTotal - others);
    const num = parseFloat(value);
    let finalValue = value;
    if (Number.isFinite(num) && num > maxAllowed + 0.001) {
      finalValue = maxAllowed > 0 ? String(Math.round(maxAllowed * 100) / 100) : "";
      showFloorWarning(floor);
    }
    setter(prev => prev.map(r => r.id === id ? { ...r, area: finalValue } : r));
  }

  // Tualetų ir katilinių sąrašas kyla iš patalpų lapo (4 puslapio) — vartotojas
  // jų čia nekuria pats, tik nurodo kiekvienam prietaisus.
  const toiletRooms = [
    ...roomsFloor1.filter(r => r.type === "Tualetas").map(r => ({ ...r, floor: 1 })),
    ...roomsFloor2.filter(r => r.type === "Tualetas").map(r => ({ ...r, floor: 2 })),
  ];
  const katilineRooms = [
    ...roomsFloor1.filter(r => r.type === "Katilinė").map(r => ({ ...r, floor: 1 })),
    ...roomsFloor2.filter(r => r.type === "Katilinė").map(r => ({ ...r, floor: 2 })),
  ];
  const kitchenRooms = [
    ...roomsFloor1.filter(r => r.type === "Svetainė/virtuvė").map(r => ({ ...r, floor: 1 })),
    ...roomsFloor2.filter(r => r.type === "Svetainė/virtuvė").map(r => ({ ...r, floor: 2 })),
  ];

  // Naujai atsiradusiems tualetams/katilinėms (pridėtiems patalpų lape) užpildoma
  // jų numatytoji santechnikos/įrangos konfigūracija; pašalintiems iš patalpų
  // sąrašo — atitinkamai išvaloma.
  useEffect(() => {
    setToiletFixtures(prev => {
      let changed = false;
      const next = { ...prev };
      toiletRooms.forEach(r => {
        if (!next[r.id]) {
          const defTypes = (DEFAULT_TOILET_FIXTURES[houseType] && DEFAULT_TOILET_FIXTURES[houseType][`floor${r.floor}`]) || [];
          next[r.id] = seedFixtures(defTypes);
          changed = true;
        }
      });
      Object.keys(next).forEach(id => {
        if (!toiletRooms.some(r => String(r.id) === id)) { delete next[id]; changed = true; }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsFloor1, roomsFloor2, houseType]);

  useEffect(() => {
    setBoilerFixtures(prev => {
      let changed = false;
      const next = { ...prev };
      katilineRooms.forEach(r => {
        if (!next[r.id]) { next[r.id] = seedFixtures(DEFAULT_BOILER_FIXTURES); changed = true; }
      });
      Object.keys(next).forEach(id => {
        if (!katilineRooms.some(r => String(r.id) === id)) { delete next[id]; changed = true; }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsFloor1, roomsFloor2]);

  useEffect(() => {
    setKitchenFixtures(prev => {
      let changed = false;
      const next = { ...prev };
      kitchenRooms.forEach(r => {
        if (!next[r.id]) { next[r.id] = seedFixtures(DEFAULT_KITCHEN_FIXTURES); changed = true; }
      });
      Object.keys(next).forEach(id => {
        if (!kitchenRooms.some(r => String(r.id) === id)) { delete next[id]; changed = true; }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsFloor1, roomsFloor2]);

  // Dešinės kolonos paletė priklauso nuo pasirinkto "taikinio" (tualeto,
  // katilinės ar virtuvės) — jei pasirinktas taikinys pašalintas ar dar
  // nepasirinktas, automatiškai parenkamas pirmas galimas.
  useEffect(() => {
    const allTargets = [
      ...toiletRooms.map(r => ({ kind: "toilet", roomId: r.id })),
      ...katilineRooms.map(r => ({ kind: "boiler", roomId: r.id })),
      ...kitchenRooms.map(r => ({ kind: "kitchen", roomId: r.id })),
    ];
    if (allTargets.length === 0) { setActiveFixtureTarget(null); return; }
    setActiveFixtureTarget(prev => {
      if (prev && allTargets.some(t => t.kind === prev.kind && t.roomId === prev.roomId)) return prev;
      return allTargets[0];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsFloor1, roomsFloor2]);

  function addFixture(setFixtures, roomId, type) {
    setFixtures(prev => ({
      ...prev,
      [roomId]: [...(prev[roomId] || []), { id: Date.now() + Math.random(), type, qty: 1, subtype: FIXTURE_SUBTYPE_OPTIONS[type] ? "" : undefined }],
    }));
  }
  function removeFixture(setFixtures, roomId, id) {
    setFixtures(prev => ({ ...prev, [roomId]: (prev[roomId] || []).filter(f => f.id !== id) }));
  }
  function updateFixtureQty(setFixtures, roomId, id, qty) {
    setFixtures(prev => ({ ...prev, [roomId]: (prev[roomId] || []).map(f => f.id === id ? { ...f, qty } : f) }));
  }
  function updateFixtureSubtype(setFixtures, roomId, id, subtype) {
    setFixtures(prev => ({ ...prev, [roomId]: (prev[roomId] || []).map(f => f.id === id ? { ...f, subtype } : f) }));
  }

  const FIXTURE_PALETTES = { toilet: TOILET_FIXTURE_TYPES, boiler: BOILER_FIXTURE_TYPES, kitchen: KITCHEN_FIXTURE_TYPES };
  const FIXTURE_SETTERS = { toilet: setToiletFixtures, boiler: setBoilerFixtures, kitchen: setKitchenFixtures };

  if (!started) {
    return (
      <div>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: "#1C1C1C", marginBottom: 20 }}>Skaičiuoklė</h1>
        <div style={{ textAlign: "center", padding: "50px 20px" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🧮</div>
          <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 20 }}>
            Prieš pradedant, paspauskite mygtuką žemiau — sukursime naują sąmatą, kurią bet kada galėsite tęsti iš „Mano sąmatos".
          </p>
          <button
            onClick={handleStart}
            style={{ background: "#735DFF", color: "#fff", border: "none", borderRadius: 8, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
          >
            Pradėti skaičiuoti
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 18, fontWeight: 700, color: "#1C1C1C", marginBottom: 20 }}>
        {step === 7 ? "Pasirinkite klasę" : `Skaičiuoklė${estimateName ? ` — ${estimateName}` : ""}`}
      </h1>

      {step === 1 && (
        <>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>Įveskite sąmatos pavadinimą</p>
          <div style={{ marginBottom: 20, maxWidth: 360 }}>
            <input
              type="text" autoFocus value={estimateName || ""}
              onChange={e => setEstimateName(e.target.value)}
              placeholder="Pvz., Mano namo sąmata"
              style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, boxSizing: "border-box" }}
            />
          </div>
          <NextButton disabled={!estimateName || !estimateName.trim()} onClick={() => setStep(2)} />
        </>
      )}

      {step === 2 && (
        <>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>Pasirinkite būsto tipą</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
            {HOUSE_TYPES.map(t => (
              <SelectCard key={t.id} icon={t.icon} label={t.label} info={t.info}
                selected={houseType === t.id} onClick={() => selectHouseType(t.id)} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <BackButton onClick={() => setStep(1)} />
            <NextButton disabled={!houseType} onClick={() => setStep(3)} />
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>Įveskite bendrą būsto plotą</p>
          {isTwoFloors ? (
            <>
              <AreaField label="1 aukšto plotas" value={area1} onChange={setArea1} />
              <AreaField label="2 aukšto plotas" value={area2} onChange={setArea2} />
            </>
          ) : (
            <AreaField label="Bendras būsto plotas" value={area1} onChange={setArea1} />
          )}
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1c1c", marginBottom: 20 }}>
            Bendras plotas: {totalArea > 0 ? totalArea.toFixed(2) : "0"} m²
          </div>
          {isTwoFloors ? (
            <>
              <AreaField label="1 aukšto vidutinis lubų aukštis" value={ceilingHeight1} onChange={setCeilingHeight1} unit="m" />
              <AreaField label="2 aukšto vidutinis lubų aukštis" value={ceilingHeight2} onChange={setCeilingHeight2} unit="m" />
            </>
          ) : (
            <AreaField label="Vidutinis lubų aukštis" value={ceilingHeight1} onChange={setCeilingHeight1} unit="m" />
          )}
          <div style={{ marginBottom: 6 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <BackButton onClick={() => setStep(2)} />
            <NextButton disabled={!areaValid} onClick={() => setStep(4)} />
          </div>
        </>
      )}

      {step === 4 && (
        <>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>Pasirinkite inžinerines sistemas, kurias norite skaičiuoti</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
            {SYSTEMS.map(s => (
              <SelectCard key={s.id} icon={s.icon} label={s.label}
                selected={systems.includes(s.id)} onClick={() => toggleSystem(s.id)} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <BackButton onClick={() => setStep(3)} />
            <NextButton disabled={systems.length === 0} onClick={() => setStep(5)} />
          </div>
        </>
      )}

      {step === 5 && (
        <>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>
            Suveskite visas patalpas. Numatytoji konfigūracija užpildyta pagal pasirinktą būsto tipą — ją galite laisvai redaguoti.
          </p>
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* Patalpų sąrašas (-ai) */}
            <div style={{ flex: "2 1 360px", minWidth: 300 }}>
              {isTwoFloors ? (
                <>
                  <FloorHeader label="1 aukštas" remaining={remainingFloor1} warning={floorWarnings[1]} />
                  <RoomList rooms={roomsFloor1}
                    onDrop={type => addRoom(1, type)}
                    onAreaChange={(id, v) => updateRoomArea(1, id, v)}
                    onRemove={id => removeRoom(1, id)}
                    showAC={systems.includes("kondicionavimas")}
                    onToggleAC={id => toggleRoomAC(1, id)} />
                  <div style={{ marginTop: 16 }}>
                    <FloorHeader label="2 aukštas" remaining={remainingFloor2} warning={floorWarnings[2]} />
                  </div>
                  <RoomList rooms={roomsFloor2}
                    onDrop={type => addRoom(2, type)}
                    onAreaChange={(id, v) => updateRoomArea(2, id, v)}
                    onRemove={id => removeRoom(2, id)}
                    showAC={systems.includes("kondicionavimas")}
                    onToggleAC={id => toggleRoomAC(2, id)} />
                </>
              ) : (
                <>
                  <FloorHeader label="Patalpos" remaining={remainingFloor1} warning={floorWarnings[1]} />
                  <RoomList rooms={roomsFloor1}
                    onDrop={type => addRoom(1, type)}
                    onAreaChange={(id, v) => updateRoomArea(1, id, v)}
                    onRemove={id => removeRoom(1, id)}
                    showAC={systems.includes("kondicionavimas")}
                    onToggleAC={id => toggleRoomAC(1, id)} />
                </>
              )}
            </div>

            {/* Patalpų tipų sąrašas dešinėje */}
            <div style={{ flex: "1 1 180px", minWidth: 180, maxWidth: 220 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>Patalpų tipai</div>
              {isTwoFloors && (
                <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                  <button onClick={() => setAddFloor(1)}
                    style={{ flex: 1, background: addFloor === 1 ? "#735DFF" : "#f3f4f6", color: addFloor === 1 ? "#fff" : "#383853", border: "none", borderRadius: 6, padding: "5px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                    1 aukštas
                  </button>
                  <button onClick={() => setAddFloor(2)}
                    style={{ flex: 1, background: addFloor === 2 ? "#735DFF" : "#f3f4f6", color: addFloor === 2 ? "#fff" : "#383853", border: "none", borderRadius: 6, padding: "5px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                    2 aukštas
                  </button>
                </div>
              )}
              {roomsConfig
                .filter(r => (isTwoFloors && addFloor === 2) ? r.floor2 : r.floor1)
                .map(r => (
                  <RoomTypeCard key={r.name} type={r.name} onAdd={() => addRoom(isTwoFloors ? addFloor : 1, r.name)} />
                ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
            <BackButton onClick={() => setStep(4)} />
            <NextButton onClick={() => setStep(6)} />
          </div>
        </>
      )}

      {step === 6 && (
        <>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>
            Nurodykite santechnikos, katilinės ir virtuvės prietaisus. Numatytoji konfigūracija užpildyta pagal
            pasirinktą būsto tipą — ją galite laisvai redaguoti.
          </p>

          {toiletRooms.length === 0 && katilineRooms.length === 0 && kitchenRooms.length === 0 ? (
            <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
              Patalpų lape nėra nei tualeto, nei katilinės, nei virtuvės — šis žingsnis nereikalingas.
            </div>
          ) : (
            <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
              {/* Prietaisų sąrašai */}
              <div style={{ flex: "2 1 360px", minWidth: 300 }}>
                {toiletRooms.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1c1c", marginBottom: 10 }}>Tualetų santechnika</div>
                    {toiletRooms.map((r, idx) => (
                      <div key={r.id} style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                          {toiletRooms.length > 1 ? `Tualetas Nr.${idx + 1}` : "Tualetas"}{isTwoFloors ? ` (${r.floor} aukštas)` : ""}
                        </div>
                        <FixtureList
                          fixtures={toiletFixtures[r.id] || []}
                          onDrop={type => addFixture(setToiletFixtures, r.id, type)}
                          onQtyChange={(id, v) => updateFixtureQty(setToiletFixtures, r.id, id, v)}
                          onSubtypeChange={(id, v) => updateFixtureSubtype(setToiletFixtures, r.id, id, v)}
                          onRemove={id => removeFixture(setToiletFixtures, r.id, id)}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {katilineRooms.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1c1c", marginBottom: 10 }}>Katilinės įranga</div>
                    {katilineRooms.map((r, idx) => (
                      <div key={r.id} style={{ marginBottom: 16 }}>
                        {katilineRooms.length > 1 && (
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                            Katilinė Nr.{idx + 1}{isTwoFloors ? ` (${r.floor} aukštas)` : ""}
                          </div>
                        )}
                        <FixtureList
                          fixtures={boilerFixtures[r.id] || []}
                          onDrop={type => addFixture(setBoilerFixtures, r.id, type)}
                          onQtyChange={(id, v) => updateFixtureQty(setBoilerFixtures, r.id, id, v)}
                          onSubtypeChange={(id, v) => updateFixtureSubtype(setBoilerFixtures, r.id, id, v)}
                          onRemove={id => removeFixture(setBoilerFixtures, r.id, id)}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {kitchenRooms.length > 0 && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1c1c", marginBottom: 10 }}>Virtuvės prietaisai</div>
                    {kitchenRooms.map((r, idx) => (
                      <div key={r.id} style={{ marginBottom: 16 }}>
                        {kitchenRooms.length > 1 && (
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
                            Virtuvė Nr.{idx + 1}{isTwoFloors ? ` (${r.floor} aukštas)` : ""}
                          </div>
                        )}
                        <FixtureList
                          fixtures={kitchenFixtures[r.id] || []}
                          onDrop={type => addFixture(setKitchenFixtures, r.id, type)}
                          onQtyChange={(id, v) => updateFixtureQty(setKitchenFixtures, r.id, id, v)}
                          onSubtypeChange={(id, v) => updateFixtureSubtype(setKitchenFixtures, r.id, id, v)}
                          onRemove={id => removeFixture(setKitchenFixtures, r.id, id)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Prietaisų tipų sąrašas dešinėje */}
              <div style={{ flex: "1 1 180px", minWidth: 180, maxWidth: 220 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 8 }}>Prietaisų tipai</div>
                {(toiletRooms.length + katilineRooms.length + kitchenRooms.length) > 1 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 20 }}>
                    {toiletRooms.map((r, idx) => {
                      const active = activeFixtureTarget?.kind === "toilet" && activeFixtureTarget.roomId === r.id;
                      return (
                        <button key={r.id} onClick={() => setActiveFixtureTarget({ kind: "toilet", roomId: r.id })}
                          style={{ background: active ? "#735DFF" : "#f3f4f6", color: active ? "#fff" : "#383853", border: "none", borderRadius: 6, padding: "6px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
                          {toiletRooms.length > 1 ? `Tualetas Nr.${idx + 1}` : "Tualetas"}{isTwoFloors ? ` (${r.floor} a.)` : ""}
                        </button>
                      );
                    })}
                    {katilineRooms.map((r, idx) => {
                      const active = activeFixtureTarget?.kind === "boiler" && activeFixtureTarget.roomId === r.id;
                      return (
                        <button key={r.id} onClick={() => setActiveFixtureTarget({ kind: "boiler", roomId: r.id })}
                          style={{ background: active ? "#735DFF" : "#f3f4f6", color: active ? "#fff" : "#383853", border: "none", borderRadius: 6, padding: "6px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
                          {katilineRooms.length > 1 ? `Katilinė Nr.${idx + 1}` : "Katilinė"}{isTwoFloors ? ` (${r.floor} a.)` : ""}
                        </button>
                      );
                    })}
                    {kitchenRooms.map((r, idx) => {
                      const active = activeFixtureTarget?.kind === "kitchen" && activeFixtureTarget.roomId === r.id;
                      return (
                        <button key={r.id} onClick={() => setActiveFixtureTarget({ kind: "kitchen", roomId: r.id })}
                          style={{ background: active ? "#735DFF" : "#f3f4f6", color: active ? "#fff" : "#383853", border: "none", borderRadius: 6, padding: "6px 8px", fontSize: 11, fontWeight: 700, cursor: "pointer", textAlign: "left" }}>
                          {kitchenRooms.length > 1 ? `Virtuvė Nr.${idx + 1}` : "Virtuvė"}{isTwoFloors ? ` (${r.floor} a.)` : ""}
                        </button>
                      );
                    })}
                  </div>
                )}
                {activeFixtureTarget && FIXTURE_PALETTES[activeFixtureTarget.kind].map(ft => (
                  <FixtureTypeCard key={ft} type={ft}
                    onAdd={() => addFixture(FIXTURE_SETTERS[activeFixtureTarget.kind], activeFixtureTarget.roomId, ft)} />
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
            <BackButton onClick={() => setStep(5)} />
            <button onClick={() => setStep(7)}
              style={{ background: "#735DFF", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Generuoti
            </button>
          </div>
        </>
      )}

      {step === 7 && (
        <>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 14 }}>Pasirinkite sąmatos klasę</p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center", marginBottom: 24 }}>
            {CLASS_OPTIONS.map(c => (
              <ClassCard key={c.id} icon={c.icon} label={c.label} info={c.info}
                selected={selectedClass === c.id} onClick={() => setSelectedClass(c.id)} />
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
            <NextButton disabled={!selectedClass} onClick={() => {}} label="Generuoti Sąmatą" />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <BackButton onClick={() => setStep(6)} />
          </div>
        </>
      )}
    </div>
  );
}
